import { lazy, Suspense, useMemo } from 'react';
import type {
  BulkApplyResult,
  BulkReferencePort,
  BulkValidatedRow,
  BulkWorkflow,
} from '../../bulk/public';
import { applyReferenceBulkRows } from '../model/referenceBulk';

const BulkPasteDialog = lazy(() => import('../../bulk/public').then((module) => ({ default: module.BulkPasteDialog })));

export type ReferenceBulkApplied = {
  succeeded: number;
  failed: number;
};

export function ReferenceBulkCreateDialog<T extends { id: string }>({
  open,
  workflow,
  referencePort,
  onClose,
  create,
  onCreated,
  onApplied,
}: {
  open: boolean;
  workflow: BulkWorkflow;
  referencePort?: BulkReferencePort;
  onClose: () => void;
  create: (row: Record<string, string>, idempotencyKey: string) => Promise<T>;
  onCreated: (items: T[]) => void;
  onApplied: (result: ReferenceBulkApplied) => void;
}) {
  const workflows = useMemo(() => [workflow], [workflow]);
  const apply = async (_operationType: string, rows: BulkValidatedRow[], batchKey: string): Promise<BulkApplyResult> => {
    const { result, created } = await applyReferenceBulkRows(rows, batchKey, create);
    if (created.length) onCreated(created);
    onApplied({ succeeded: created.length, failed: result.rows.length - created.length });
    return result;
  };

  if (!open) return null;

  return (
    <Suspense fallback={null}>
      <BulkPasteDialog
        open
        workflows={workflows}
        referencePort={referencePort}
        initialOperationType={workflow.operationType}
        onClose={onClose}
        onApply={apply}
      />
    </Suspense>
  );
}
